import React, { useEffect, useRef, useState } from 'react'
import { useQuery } from '../hooks/useApi.js'
import { promosApi } from '../lib/api.js'
import { Spinner, Badge } from '@betina/ui'

const STORE_LABELS = {
  delhaize: 'Delhaize',
  colruyt: 'Colruyt',
  lidl: 'Lidl',
  carrefour: 'Carrefour',
  'albert-heijn': 'Albert Heijn',
  aldi: 'Aldi',
}

const STORE_COLORS = {
  delhaize: 'bg-red-50 border-red-200',
  colruyt: 'bg-blue-50 border-blue-200',
  lidl: 'bg-yellow-50 border-yellow-200',
  carrefour: 'bg-red-50 border-red-200',
  'albert-heijn': 'bg-blue-50 border-blue-200',
  aldi: 'bg-sky-50 border-sky-200',
}

const STORE_HEADER_COLORS = {
  delhaize: 'bg-red-600',
  colruyt: 'bg-blue-700',
  lidl: 'bg-yellow-400 text-gray-900',
  carrefour: 'bg-red-500',
  'albert-heijn': 'bg-blue-500',
  aldi: 'bg-sky-600',
}

export function Promos() {
  const [selectedStore, setSelectedStore] = useState(null)
  // states: null | 'loading' | 'scraping' | 'ok' | 'error' | 'timeout'
  const [triggerStatus, setTriggerStatus] = useState(null)
  const [pollAttempt, setPollAttempt] = useState(0)
  const pollRef = useRef(null)

  const { data, loading, error, refetch } = useQuery(() => promosApi.getAll(), [])

  // Stop polling on unmount.
  useEffect(() => () => clearTimeout(pollRef.current), [])

  function stopPolling() {
    clearTimeout(pollRef.current)
    pollRef.current = null
  }

  async function handleTrigger() {
    stopPolling()
    setTriggerStatus('loading')
    setPollAttempt(0)
    let beforeCount = 0
    try {
      const before = await promosApi.getAll()
      beforeCount = (before?.stores ?? []).reduce((s, st) => s + (st.promos?.length ?? 0), 0)
      await promosApi.trigger()
      setTriggerStatus('scraping')
      // Poll every 20s for up to 6 min (~18 attempts).
      const tick = async (attempt) => {
        setPollAttempt(attempt)
        try {
          const fresh = await promosApi.getAll()
          const freshCount = (fresh?.stores ?? []).reduce((s, st) => s + (st.promos?.length ?? 0), 0)
          if (freshCount !== beforeCount && freshCount > 0) {
            await refetch()
            setTriggerStatus('ok')
            setTimeout(() => setTriggerStatus(null), 4000)
            return
          }
        } catch {
          // ignore transient errors during polling
        }
        if (attempt >= 18) {
          setTriggerStatus('timeout')
          setTimeout(() => setTriggerStatus(null), 5000)
          return
        }
        pollRef.current = setTimeout(() => tick(attempt + 1), 20_000)
      }
      pollRef.current = setTimeout(() => tick(1), 20_000)
    } catch (e) {
      setTriggerStatus('error')
      setTimeout(() => setTriggerStatus(null), 4000)
    }
  }

  function buttonLabel() {
    if (triggerStatus === 'loading') return '⏳ Démarrage…'
    if (triggerStatus === 'scraping') return `🔄 Collecte en cours… (${pollAttempt * 20}s)`
    if (triggerStatus === 'ok') return '✓ Mis à jour !'
    if (triggerStatus === 'timeout') return '⚠ Toujours en cours…'
    if (triggerStatus === 'error') return '✗ Erreur'
    return '↻ Actualiser'
  }

  function buttonClass() {
    const base = 'text-xs px-3 py-1.5 rounded-full font-medium transition-colors '
    if (triggerStatus === 'ok') return base + 'bg-emerald-100 text-emerald-700'
    if (triggerStatus === 'error') return base + 'bg-red-100 text-red-700'
    if (triggerStatus === 'timeout') return base + 'bg-amber-100 text-amber-700'
    if (triggerStatus === 'scraping' || triggerStatus === 'loading') return base + 'bg-blue-100 text-blue-700'
    return base + 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300'
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Spinner />
        <p className="text-sm text-gray-400">Chargement des promos…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-700 font-medium mb-1">Impossible de charger les promos</p>
          <p className="text-red-500 text-xs mb-3">{error.message}</p>
          <button
            onClick={refetch}
            className="text-sm text-red-600 underline"
          >
            Réessayer
          </button>
        </div>
      </div>
    )
  }

  const stores = data?.stores ?? []
  const dateLabel = data?.date ? formatDate(data.date) : '—'
  const totalPromos = stores.reduce((sum, s) => sum + (s.promos?.length ?? 0), 0)

  const filteredStores = selectedStore
    ? stores.filter((s) => s.store === selectedStore)
    : stores

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Promos Supermarchés</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            {dateLabel} · {totalPromos} promotion{totalPromos !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleTrigger}
          disabled={triggerStatus === 'loading' || triggerStatus === 'scraping'}
          className={buttonClass()}
        >
          {buttonLabel()}
        </button>
      </div>

      {/* Status banner during scraping */}
      {triggerStatus === 'scraping' && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 flex items-center gap-2">
          <Spinner size="sm" />
          <span>Collecte en cours sur les 6 magasins (~3-5 min). On rafraîchit dès que c'est fini.</span>
        </div>
      )}
      {triggerStatus === 'timeout' && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          La collecte continue en arrière-plan. Reviens dans quelques minutes ou clique sur ↻ pour relancer.
        </div>
      )}

      {/* Store filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-none">
        <button
          onClick={() => setSelectedStore(null)}
          className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-medium border transition-colors ${
            !selectedStore
              ? 'bg-emerald-600 text-white border-emerald-600'
              : 'bg-white text-gray-500 border-gray-200'
          }`}
        >
          Tous ({stores.length})
        </button>
        {stores.map((s) => (
          <button
            key={s.store}
            onClick={() => setSelectedStore(s.store === selectedStore ? null : s.store)}
            className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-medium border transition-colors ${
              selectedStore === s.store
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-gray-500 border-gray-200'
            }`}
          >
            {STORE_LABELS[s.store] ?? s.store} ({s.promos?.length ?? 0})
          </button>
        ))}
      </div>

      {/* Store cards */}
      {stores.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <p className="text-3xl mb-2">🏪</p>
          <p className="text-sm">Aucune promo disponible pour aujourd'hui.</p>
          <p className="text-xs mt-1">Essayez d'actualiser pour lancer la collecte.</p>
        </div>
      ) : (
        filteredStores.map((store) => (
          <StoreCard key={store.store} store={store} />
        ))
      )}
    </div>
  )
}

function StoreCard({ store }) {
  const [expanded, setExpanded] = useState(true)
  const label = STORE_LABELS[store.store] ?? store.store
  const headerClass = STORE_HEADER_COLORS[store.store] ?? 'bg-gray-700'
  const borderClass = STORE_COLORS[store.store] ?? 'bg-gray-50 border-gray-200'
  const promos = store.promos ?? []

  if (store.error) {
    return (
      <div className={`rounded-xl border overflow-hidden ${borderClass}`}>
        <div className={`px-4 py-2.5 text-white text-sm font-semibold ${headerClass}`}>
          🔴 {label}
        </div>
        <div className="p-3 text-red-700 text-xs bg-red-50">
          Erreur : {store.error}
        </div>
      </div>
    )
  }

  return (
    <div className={`rounded-xl border overflow-hidden ${borderClass}`}>
      {/* Store header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold ${headerClass}`}
      >
        <span>🏪 {label}</span>
        <span className="flex items-center gap-2">
          <span className="text-xs font-normal opacity-80">{promos.length} promos</span>
          <span className="text-xs opacity-70">{expanded ? '▲' : '▼'}</span>
        </span>
      </button>

      {/* Promo list */}
      {expanded && (
        <div className="divide-y divide-gray-100">
          {promos.length === 0 ? (
            <p className="px-4 py-3 text-xs text-gray-400">Aucune promotion récupérée.</p>
          ) : (
            promos.slice(0, 40).map((p, i) => <PromoRow key={i} promo={p} />)
          )}
          {promos.length > 40 && (
            <p className="px-4 py-2 text-xs text-gray-400 text-center">
              + {promos.length - 40} autres promotions
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function PromoRow({ promo }) {
  return (
    <div className="flex items-start gap-2 px-4 py-2.5">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 font-medium leading-snug truncate">
          {promo.name}
          {promo.brand && (
            <span className="ml-1 text-xs text-gray-400 font-normal">{promo.brand}</span>
          )}
        </p>
        {promo.weight && (
          <p className="text-xs text-gray-400 mt-0.5">{promo.weight}</p>
        )}
        {promo.promoType && (
          <span className="inline-block text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-1 font-medium">
            {promo.promoType}
          </span>
        )}
        {promo.requiresLoyaltyCard && (
          <span className="inline-block ml-1 text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5 mt-1">
            🎫 carte
          </span>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        {promo.price != null && (
          <p className="text-sm font-bold text-gray-900">{Number(promo.price).toFixed(2)} €</p>
        )}
        {promo.originalPrice != null && promo.originalPrice !== promo.price && (
          <p className="text-xs text-gray-400 line-through">
            {Number(promo.originalPrice).toFixed(2)} €
          </p>
        )}
      </div>
    </div>
  )
}

function formatDate(dateKey) {
  const [y, m, d] = dateKey.split('/')
  return `${d}/${m}/${y}`
}
